/**
 * AgentChat - 训练广场单智能体对话主组件
 * 
 * 替代原有的 iframe 嵌入方式，提供原生对话体验：
 * - SSE 流式 AI 回复
 * - Markdown 渲染（代码高亮、Mermaid、KaTeX）
 * - 虚拟滚动消息列表
 * - 历史会话管理（进入自动恢复、列表切换、删除）
 */

import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import EnhancedMarkdown from '../enhanced/EnhancedMarkdown';
import ChatInput from './ChatInput';
import HistoryPanel from './HistoryPanel';
import useAgentChat from './useAgentChat';
import IconMapper from '../IconMapper';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import HistoryIcon from '@mui/icons-material/History';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import './AgentChat.css';

const GUIDE_INTRO_IMAGES = [
  {
    src: '/pics/三元范式.png',
    title: '三元范式',
    desc: '教师当编剧，学生练工程，AI 组织虚拟工程社会。',
  },
  {
    src: '/pics/功能闭环.png',
    title: '功能构成',
    desc: '看懂教师编剧、学生训练、实验验证、过程评价如何形成闭环。',
  },
  {
    src: '/pics/youlaber.png',
    title: 'YouLabber 实验员',
    desc: '把学生方案推向执行验证，让方案能跑、能验、能改。',
  },
  {
    src: '/pics/AI评价.png',
    title: 'AI 过程评价',
    desc: '从训练存档、对话和修改轨迹中形成能力画像与改进建议。',
  },
  {
    src: '/pics/创新对比.png',
    title: '范式区别',
    desc: '区别普通 AI 工具的“给答案”和邮百工的“练判断”。',
  },
  {
    src: '/pics/四重变革.png',
    title: '四重变革',
    desc: '从场景、师资、理念和产教融合理解邮百工的教育价值。',
  },
];

const AgentChat = ({
  agent,
  user,
  expertServiceUrl,
  onBack,
  flowName = '',
  projectName = '训练广场',
  trainSessionId = '',
  onA2UIAction = null,
  welcomeContentOverride = '',
  variant = 'default',
  guideDocLinks = [],
  headerAccessory = null,
}) => {
  const { t } = useTranslation();
  const isGuideVariant = variant === 'guide';
  // 与 GroupChatHub RoomLobby 严格对齐，确保 token 记录与查询使用同一 ID
  const userId = String(
    user?.unified_user_id || user?.userId || user?.studentID || user?.student_id || user?.username || 'anonymous'
  );
  const userType = user?.user_type || (user?.isSchoolUser ? 'campus' : user?.isCasUser ? 'bupt_cas' : 'guest');
  const [showHistory, setShowHistory] = useState(!isGuideVariant);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const isProgrammaticScrollRef = useRef(false);
  const [isAutoScroll, setIsAutoScroll] = useState(true);
  // 常用指南：默认折叠
  const [showGuideDocs, setShowGuideDocs] = useState(false);
  const [guideImageIndex, setGuideImageIndex] = useState(0);
  const [guideImageLightboxOpen, setGuideImageLightboxOpen] = useState(false);

  const {
    messages,
    isStreaming,
    error,
    sessionId,
    sessions,
    isLoadingSessions,
    isInitialLoading,
    quotaBlocked,
    sendMessage,
    stopStreaming,
    newConversation,
    loadSessions,
    loadSession,
    deleteSession,
    clearAllSessions,
    clearError,
    saveArchive,
  } = useAgentChat({
    expertServiceUrl,
    agent,
    userId,
    userType,
    trainSessionId,
  });

  // 只滚动消息容器本身，避免 scrollIntoView 带动父级（课程页/导览栏）连环滚动
  const scrollMessagesToBottom = useCallback((behavior = 'auto') => {
    const container = messagesContainerRef.current;
    if (!container) return;
    isProgrammaticScrollRef.current = true;
    const apply = () => {
      container.scrollTop = container.scrollHeight;
    };
    if (behavior === 'smooth' && typeof container.scrollTo === 'function') {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    } else {
      apply();
    }
    // 程序滚动期间忽略 onScroll，防止 isAutoScroll 抖动触发循环
    window.setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, behavior === 'smooth' ? 320 : 50);
  }, []);

  // 自动滚动到底部（导览员用瞬时滚动，避免 smooth 叠加造成「疯狂下滚」）
  useEffect(() => {
    if (!isAutoScroll) return;
    scrollMessagesToBottom(isGuideVariant ? 'auto' : 'smooth');
  }, [messages, isAutoScroll, isGuideVariant, scrollMessagesToBottom]);

  // 监听滚动，判断是否在底部
  const handleScroll = useCallback(() => {
    if (isProgrammaticScrollRef.current) return;
    const container = messagesContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 60;
    setIsAutoScroll(isAtBottom);
  }, []);

  // 切换历史面板时加载会话列表
  const toggleHistory = useCallback(() => {
    setShowHistory(prev => {
      if (!prev) {
        loadSessions();
      }
      return !prev;
    });
  }, [loadSessions]);

  // 选择历史会话
  const handleSelectSession = useCallback((sid) => {
    loadSession(sid);
    setIsAutoScroll(true);
  }, [loadSession]);

  // 新建对话
  const handleNewConversation = useCallback(() => {
    newConversation();
    setIsAutoScroll(true);
  }, [newConversation]);

  const handleA2UIAction = useCallback((action) => {
    const handled = onA2UIAction ? onA2UIAction(action) : false;
    if (handled) return;

    if (action?.autoSend && action?.displayText) {
      setIsAutoScroll(true);
      sendMessage(action.displayText);
      setTimeout(() => {
        scrollMessagesToBottom(isGuideVariant ? 'auto' : 'smooth');
      }, 80);
    }
  }, [onA2UIAction, sendMessage, scrollMessagesToBottom, isGuideVariant]);

  // 删除会话
  const handleDeleteSession = useCallback(async (sid) => {
    return await deleteSession(sid);
  }, [deleteSession]);

  const handleClearGuideHistory = useCallback(async () => {
    const okToClear = window.confirm('确定清空导览员与 ExpertService 的历史对话记录吗？清空后会回到初始导览状态。');
    if (!okToClear) return;
    const ok = await clearAllSessions();
    if (ok) {
      setShowHistory(false);
      setIsAutoScroll(true);
    }
  }, [clearAllSessions]);

  // 保存存档
  const [saveArchiveStatus, setSaveArchiveStatus] = useState(null); // { ok, message }
  const handleSaveArchive = useCallback(async () => {
    if (!sessionId || messages.length === 0) {
      setSaveArchiveStatus({ ok: false, message: t('agent_chat_save_archive_no_session', '请先进行对话后再保存') });
      setTimeout(() => setSaveArchiveStatus(null), 3000);
      return;
    }
    setSaveArchiveStatus(null);
    const result = await saveArchive({
      flowName: flowName || (agent?.flow?.[0] || agent?.flowTags?.[0] || ''),
      projectName: projectName || `单智能体-${agent?.name || ''}`,
      trainSessionId,
      unifiedUserID: user?.unified_user_id || '',
      userType,
      username: user?.username || '',
    });
    setSaveArchiveStatus(result);
    if (result.ok) setTimeout(() => setSaveArchiveStatus(null), 3000);
  }, [sessionId, messages.length, saveArchive, flowName, projectName, trainSessionId, agent, user, userType, t]);

  // 欢迎消息
  const welcomeContent = useMemo(() => {
    if (welcomeContentOverride) return welcomeContentOverride;
    if (!agent) return '';
    return `### ${t('agent_chat_welcome_hello', { name: agent.name })}\n\n${agent.description || t('agent_chat_welcome_help')}\n\n---\n*${t('agent_chat_welcome_start')}*`;
  }, [agent, t, welcomeContentOverride]);

  const currentGuideImage = GUIDE_INTRO_IMAGES[guideImageIndex];

  const showPrevGuideImage = useCallback(() => {
    setGuideImageIndex(prev => (prev - 1 + GUIDE_INTRO_IMAGES.length) % GUIDE_INTRO_IMAGES.length);
  }, []);

  const showNextGuideImage = useCallback(() => {
    setGuideImageIndex(prev => (prev + 1) % GUIDE_INTRO_IMAGES.length);
  }, []);

  const openGuideImageLightbox = useCallback(() => {
    setGuideImageLightboxOpen(true);
  }, []);

  const closeGuideImageLightbox = useCallback(() => {
    setGuideImageLightboxOpen(false);
  }, []);

  useEffect(() => {
    if (!guideImageLightboxOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setGuideImageLightboxOpen(false);
      } else if (event.key === 'ArrowLeft') {
        showPrevGuideImage();
      } else if (event.key === 'ArrowRight') {
        showNextGuideImage();
      }
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [guideImageLightboxOpen, showPrevGuideImage, showNextGuideImage]);

  const toggleGuideDocs = useCallback(() => {
    setShowGuideDocs((prev) => !prev);
  }, []);

  const renderGuideDocsFooter = () => {
    if (!isGuideVariant || !guideDocLinks.length) return null;
    return (
      <div className={`guide-docs-footer${showGuideDocs ? ' is-expanded' : ' is-collapsed'}`}>
        <button
          type="button"
          className="guide-docs-footer-toggle"
          onClick={toggleGuideDocs}
          aria-expanded={showGuideDocs}
        >
          <span className="guide-docs-footer-label">
            <MenuBookOutlinedIcon className="guide-docs-footer-icon" fontSize="inherit" />
            常用指南
            <em>{guideDocLinks.length}</em>
          </span>
          <span className="guide-docs-footer-chevron" aria-hidden="true">
            {showGuideDocs ? '收起' : '展开'}
          </span>
        </button>
        {showGuideDocs ? (
          <div className="guide-docs-footer-grid">
            {guideDocLinks.map((link) => (
              <a
                key={link.url}
                className="guide-docs-footer-card"
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="guide-docs-footer-card-title">{link.title}</span>
                <span className="guide-docs-footer-card-sub">{link.subtitle}</span>
              </a>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  const renderAgentResourceCard = () => {
    if (!agent) return null;
    if (isGuideVariant) return null;
    const flowTags = Array.isArray(agent.flow) ? agent.flow : [];
    return (
      <div className="agent-resource-card">
        <div className="agent-resource-kicker">单智能体资源</div>
        <div className="agent-resource-title">{agent.name}</div>
        {agent.description && <div className="agent-resource-desc">{agent.description}</div>}
        <div className="agent-resource-tags">
          {agent.category && <span>{agent.category}</span>}
          {agent.duration && <span>{agent.duration}</span>}
          {flowTags.slice(0, 3).map((tag) => <span key={tag}>{tag}</span>)}
          {agent.plazaAgentReleaseId && <span>市集版本 #{agent.plazaAgentReleaseId}</span>}
        </div>
        <div className="agent-resource-hint">
          建议先说明你的任务背景、已有材料和当前卡点，再让智能体给出针对性建议。
        </div>
      </div>
    );
  };

  const renderGuideIntroCard = () => {
    if (!isGuideVariant) return null;
    return (
      <div className="agent-chat-guide-history-card">
        <div className="guide-entry-hero">
          <div className="guide-entry-kicker">入场导览</div>
          <h2>这里不是普通 AI 助教，而是工程训练场</h2>
          <p>教师当编剧，学生练工程，AI 组织导师、专家、陪练、实验员和评审，陪你把判断过程练出来。</p>
          <div className="guide-image-carousel" aria-label="邮百工导览图卡">
            <button
              className="guide-image-nav guide-image-nav-prev"
              type="button"
              onClick={showPrevGuideImage}
              aria-label="上一张导览图"
            >
              ‹
            </button>
            <button
              type="button"
              className="guide-paradigm-figure"
              onClick={openGuideImageLightbox}
              aria-label={`放大查看：${currentGuideImage.title}`}
              title="点击放大查看"
            >
              <img src={currentGuideImage.src} alt={`邮百工导览图：${currentGuideImage.title}`} />
              <span className="guide-image-zoom-hint">点击放大</span>
            </button>
            <button
              className="guide-image-nav guide-image-nav-next"
              type="button"
              onClick={showNextGuideImage}
              aria-label="下一张导览图"
            >
              ›
            </button>
            <div className="guide-image-caption">
              <strong>{currentGuideImage.title}</strong>
              <span>{currentGuideImage.desc}</span>
            </div>
            <div className="guide-image-dots">
              {GUIDE_INTRO_IMAGES.map((image, index) => (
                <button
                  key={image.src}
                  className={`guide-image-dot ${index === guideImageIndex ? 'active' : ''}`}
                  type="button"
                  onClick={() => setGuideImageIndex(index)}
                  title={image.title}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="guide-persistent-welcome">
          <EnhancedMarkdown content={welcomeContent} onA2UIAction={handleA2UIAction} />
        </div>
      </div>
    );
  };

  const renderGuideImageLightbox = () => {
    if (!guideImageLightboxOpen || !currentGuideImage || typeof document === 'undefined') {
      return null;
    }
    return createPortal(
      (
        <div
          className="guide-image-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`放大查看：${currentGuideImage.title}`}
          onClick={closeGuideImageLightbox}
        >
          <button
            type="button"
            className="guide-image-lightbox-close"
            onClick={closeGuideImageLightbox}
            aria-label="关闭放大预览"
          >
            <CloseIcon fontSize="small" />
          </button>
          <button
            type="button"
            className="guide-image-lightbox-nav guide-image-lightbox-prev"
            onClick={(event) => {
              event.stopPropagation();
              showPrevGuideImage();
            }}
            aria-label="上一张导览图"
          >
            ‹
          </button>
          <div
            className="guide-image-lightbox-stage"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={currentGuideImage.src}
              alt={`邮百工导览图：${currentGuideImage.title}`}
            />
            <div className="guide-image-lightbox-caption">
              <strong>{currentGuideImage.title}</strong>
              <span>{currentGuideImage.desc}</span>
            </div>
          </div>
          <button
            type="button"
            className="guide-image-lightbox-nav guide-image-lightbox-next"
            onClick={(event) => {
              event.stopPropagation();
              showNextGuideImage();
            }}
            aria-label="下一张导览图"
          >
            ›
          </button>
        </div>
      ),
      document.body
    );
  };

  if (!agent) return null;

  return (
    <div className={`agent-chat-container ${isGuideVariant ? 'agent-chat-guide' : ''}`}>
      {renderGuideImageLightbox()}
      {/* 头部信息栏 */}
      <div className="agent-chat-header">
        <div className="agent-chat-header-left">
          {onBack && (
            <button className="agent-chat-back-btn" onClick={onBack} title={t('agent_chat_back')}>
              <ArrowBackIosNewIcon fontSize="small" />
            </button>
          )}
          <div className={`agent-chat-avatar ${agent.iconColor || ''}`}>
            <IconMapper iconName={agent.icon} fontSize="small" />
          </div>
          <div className="agent-chat-header-info">
            <h3 className="agent-chat-header-name">
              {agent.name}
              {agent.category && <span className="agent-chat-tag">{agent.category}</span>}
            </h3>
            {isGuideVariant && (
              <div className="agent-chat-header-desc">帮你从“找答案”进入“练判断”</div>
            )}
          </div>
        </div>
        {isGuideVariant ? (
          <div className="agent-chat-header-actions">
            {headerAccessory}
            <button
              className="agent-chat-header-btn agent-chat-guide-clear-btn"
              onClick={handleClearGuideHistory}
              title="清空导览记录"
              disabled={isStreaming}
            >
              <DeleteSweepOutlinedIcon fontSize="small" />
            </button>
          </div>
        ) : (
          <div className="agent-chat-header-actions">
            {headerAccessory}
            <button
              className="agent-chat-header-btn agent-chat-header-action"
              onClick={handleSaveArchive}
              title={t('agent_chat_save_archive', '保存存档')}
              disabled={!sessionId || messages.length === 0}
            >
              <SaveOutlinedIcon fontSize="small" />
              <span>保存</span>
            </button>
            <button
              className="agent-chat-header-btn agent-chat-header-action"
              onClick={handleNewConversation}
              title={t('agent_chat_new')}
            >
              <AddCommentOutlinedIcon fontSize="small" />
              <span>新建</span>
            </button>
            <button
              className={`agent-chat-header-btn agent-chat-header-action ${showHistory ? 'active' : ''}`}
              onClick={toggleHistory}
              title={t('agent_chat_history')}
            >
              <HistoryIcon fontSize="small" />
              <span>历史会话</span>
            </button>
          </div>
        )}
      </div>

      {/* 保存存档状态提示 */}
      {saveArchiveStatus && (
        <div className={`agent-chat-save-status ${saveArchiveStatus.ok ? 'success' : 'error'}`}>
          {saveArchiveStatus.message}
        </div>
      )}

      {/* 主体区域 */}
      <div className="agent-chat-body">
        {/* 历史面板 */}
        {showHistory && (
          <HistoryPanel
            sessions={sessions}
            isLoading={isLoadingSessions}
            currentSessionId={sessionId}
            onSelect={handleSelectSession}
            onNewChat={handleNewConversation}
            onClose={() => setShowHistory(false)}
            onDelete={handleDeleteSession}
          />
        )}

        {/* 消息区域 */}
        <div className="agent-chat-messages-area">
          <div
            className="agent-chat-messages"
            ref={messagesContainerRef}
            onScroll={handleScroll}
          >
            {/* 初始加载状态 */}
            {isInitialLoading && (
              <div className="agent-chat-initial-loading">
                <CircularProgress size={24} sx={{ color: 'var(--agent-chat-primary)' }} />
                <span>{t('agent_chat_loading')}</span>
              </div>
            )}

            {renderGuideIntroCard()}

            {/* 欢迎消息（仅无消息且非加载中时显示） */}
            {!isGuideVariant && !isInitialLoading && messages.length === 0 && (
              <div className="agent-chat-welcome">
                {!isGuideVariant && (
                  <div className={`agent-chat-welcome-icon ${agent.iconColor || ''}`}>
                    <IconMapper iconName={agent.icon} fontSize="large" />
                  </div>
                )}
                <div className="agent-chat-welcome-content">
                  {renderAgentResourceCard()}
                  <EnhancedMarkdown content={welcomeContent} onA2UIAction={handleA2UIAction} />
                </div>
              </div>
            )}

            {/* 消息列表 */}
            {messages.map((msg, index) => (
              <MessageBubble
                key={`${msg.role}-${index}-${msg.timestamp}`}
                message={msg}
                agentName={agent.name}
                agentIcon={agent.icon}
                agentIconColor={agent.iconColor}
                onA2UIAction={handleA2UIAction}
              />
            ))}

            {/* 错误提示 */}
            {error && (
              <div className="agent-chat-error">
                <ErrorOutlineIcon fontSize="small" />
                <span>{error}</span>
                <button onClick={clearError} className="agent-chat-error-close">
                  <CloseIcon fontSize="small" />
                </button>
              </div>
            )}

            {/* 滚动锚点 */}
            <div ref={messagesEndRef} />
          </div>

          {/* 额度阻断提示 */}
          {quotaBlocked && (
            <div className="agent-chat-quota-blocked">
              {t('agent_chat_quota_blocked')}
            </div>
          )}

          {/* 输入区域 */}
          <ChatInput
            onSend={sendMessage}
            isStreaming={isStreaming}
            onStop={stopStreaming}
            placeholder={quotaBlocked ? t('agent_chat_quota_placeholder') : t('agent_chat_ask', { name: agent.name })}
            disabled={quotaBlocked}
          />

          {renderGuideDocsFooter()}
        </div>
      </div>
    </div>
  );
};

/**
 * 单条消息气泡组件
 */
const MessageBubble = React.memo(({ message, agentName, agentIcon, agentIconColor, onA2UIAction }) => {
  const { t } = useTranslation();
  const isUser = message.role === 'user';

  return (
    <div className={`agent-chat-message ${isUser ? 'user' : 'assistant'}`}>
      {/* 头像 */}
      <div className={`agent-chat-msg-avatar ${isUser ? '' : agentIconColor || ''}`}>
        {isUser ? (
          <PersonOutlineIcon fontSize="small" />
        ) : (
          <SmartToyOutlinedIcon fontSize="small" />
        )}
      </div>

      {/* 消息内容 */}
      <div className="agent-chat-msg-body">
        <div className="agent-chat-msg-header">
          <span className="agent-chat-msg-name">
            {isUser ? t('agent_chat_me') : agentName}
          </span>
          {message.timestamp && (
            <span className="agent-chat-msg-time">
              {formatTime(message.timestamp)}
            </span>
          )}
        </div>
        <div className="agent-chat-msg-content">
          {isUser ? (
            <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
              {message.content}
            </ReactMarkdown>
          ) : (
            <>
              {message.content ? (
                <EnhancedMarkdown
                  content={message.content}
                  onA2UIAction={onA2UIAction}
                  isStreaming={Boolean(message.isStreaming)}
                />
              ) : message.isStreaming ? (
                <div className="agent-chat-typing">
                  <span className="typing-dot"></span>
                  <span className="typing-dot"></span>
                  <span className="typing-dot"></span>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
});

/**
 * 格式化时间显示
 */
function formatTime(timestamp) {
  if (!timestamp) return '';
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export default React.memo(AgentChat);
